/**
 * The drawer's device header — mirrors the approved mockup
 * (`fleet-device-support.html:120-147`): name, `location · tenant`, mono
 * serial line, a pill row, and six fact tiles (App, Target, Ring, Terminal,
 * Uptime, Storage).
 *
 * `FleetRow` supplies App/Target/Ring and the "online" pill (from
 * `updatedAt`) — those are known-good, server-sourced columns the Fleet
 * table already renders. Terminal/Uptime/Storage and the Device
 * Owner/Kiosk-locked/ADB/unsynced-sales pills come from the latest `done`
 * `device_report` command's `result`, which is a free-form
 * `z.record(z.unknown())` on the wire (`FleetCommandSchema.result` in
 * shared-types) — nothing constrains its keys yet. Every read here is
 * optional-chained against a small set of plausible key names and falls back
 * to '—' / is omitted, NEVER a fabricated zero or a guessed "off". This is
 * unverified against a real device_report payload (no live admin session was
 * obtainable to run one) — if the real shape uses different keys, this is
 * the first place to fix once Tier 1 is exercised against real hardware.
 */
import type React from 'react';
import type { FleetCommand } from '@platform/shared-types';
import type { FleetRow } from './fleetRows';

const PILL: Record<'ok' | 'warn' | 'bad' | 'mute', string> = {
  ok: 'bg-emerald-500/15 text-emerald-600',
  warn: 'bg-amber-500/15 text-amber-600',
  bad: 'bg-rose-500/15 text-rose-600',
  mute: 'bg-gray-400/20 text-gray-500',
};

const Pill = ({ tone, children }: { tone: keyof typeof PILL; children: React.ReactNode }) => (
  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-extrabold ${PILL[tone]}`}>
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {children}
  </span>
);

/** A device seen this recently reads "Online". A guess — nothing in the plan pins the threshold. */
const ONLINE_WITHIN_MS = 2 * 60 * 1000;

function agoLabel(at: number, now: number): string {
  const ms = Math.max(0, now - at);
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return `${h}h ago`;
}

const bool = (v: unknown): boolean | undefined => (typeof v === 'boolean' ? v : undefined);
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

function fmtBytes(n: number | undefined): string | undefined {
  if (n == null) return undefined;
  const gb = n / (1024 * 1024 * 1024);
  return `${gb.toFixed(1)} GB free`;
}

function fmtUptime(seconds: number | undefined): string | undefined {
  if (seconds == null) return undefined;
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
}

function Fact({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2">
      <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{k}</div>
      <div className={`mt-0.5 text-[13px] font-semibold text-gray-900 ${mono ? 'font-mono text-[12px] font-medium' : ''}`}>{v}</div>
    </div>
  );
}

export default function DeviceIdentityCard({ row, latestDeviceReport, now = Date.now() }: {
  row: FleetRow;
  /** The most recent terminal `device_report` command (any state; only a `done` one has a usable result). */
  latestDeviceReport?: FleetCommand;
  now?: number;
}) {
  const result = (latestDeviceReport?.state === 'done' ? latestDeviceReport.result : null) as Record<string, unknown> | null;

  const online = !!row.updatedAt && now - row.updatedAt < ONLINE_WITHIN_MS;
  const onlinePill = row.updatedAt
    ? <Pill tone={online ? 'ok' : 'bad'}>{online ? 'Online' : 'Offline'} · {agoLabel(row.updatedAt, now)}</Pill>
    : <Pill tone="mute">Never checked in</Pill>;

  const deviceOwner = bool(result?.deviceOwner);
  const kioskLocked = bool(result?.kioskLocked);
  const adbEnabled = bool(result?.adbEnabled);
  const unsyncedCount = num(result?.unsyncedSaleCount);

  const terminalStatus = str((result?.terminal as any)?.status) ?? str(result?.terminalStatus);
  const terminalVendor = str((result?.terminal as any)?.vendor) ?? str(result?.terminalVendor);
  const terminalFact = terminalStatus
    ? `${terminalVendor ? `${terminalVendor} · ` : ''}${terminalStatus}`
    : '—';

  const uptimeFact = fmtUptime(num(result?.uptimeSeconds)) ?? '—';
  const storageFact = fmtBytes(num(result?.storageFreeBytes) ?? num(result?.freeStorageBytes)) ?? '—';

  const serialLine = [row.serialNumber, row.hardwareModel, row.hardwareBrand, row.deviceType]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
        <h2 className="text-[13px] font-extrabold text-gray-900">Device</h2>
        <span className="text-[11.5px] text-gray-400 truncate">{row.deviceId ? `${row.deviceId} · deep-linked from the Fleet table` : 'unit not yet checked in'}</span>
      </div>
      <div className="p-4">
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-[220px] flex-1">
            <div className="text-[17px] font-extrabold tracking-tight text-gray-900">
              {row.registerName || row.deviceId || row.unitId || 'Unnamed device'}
            </div>
            <div className="mt-0.5 text-[13px] text-gray-600">
              {row.locationName || '—'}
            </div>
            {serialLine && <div className="mt-1 font-mono text-[11px] text-gray-400">{serialLine}</div>}
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {onlinePill}
              {deviceOwner !== undefined && <Pill tone={deviceOwner ? 'ok' : 'warn'}>{deviceOwner ? 'Device Owner' : 'Not Device Owner'}</Pill>}
              {kioskLocked !== undefined && <Pill tone={kioskLocked ? 'ok' : 'warn'}>{kioskLocked ? 'Kiosk locked' : 'Kiosk unlocked'}</Pill>}
              {adbEnabled !== undefined && <Pill tone={adbEnabled ? 'warn' : 'ok'}>{adbEnabled ? 'ADB enabled' : 'ADB off'}</Pill>}
              {unsyncedCount !== undefined && unsyncedCount > 0 && (
                <Pill tone="bad">{unsyncedCount} sale{unsyncedCount === 1 ? '' : 's'} unsynced</Pill>
              )}
            </div>
          </div>

          <div className="grid flex-[2] min-w-[260px] grid-cols-2 gap-2 sm:grid-cols-3">
            <Fact k="App" v={row.displayVersion ? row.displayVersion : '—'} mono />
            <Fact k="Target" v={row.targetVersion || '—'} mono />
            <Fact k="Ring" v={row.releaseRing || '—'} />
            <Fact k="Terminal" v={terminalFact} />
            <Fact k="Uptime" v={uptimeFact} />
            <Fact k="Storage" v={storageFact} />
          </div>
        </div>
      </div>
    </div>
  );
}
